import { useState } from "react";
import { Link } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { Menu, X, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ACCENT } from "@/lib/siteTheme";
import type { ActiveNav } from "./SiteHeader";

const caseStudyLinks = [
  { to: "/case-studies#seo", label: "Local SEO" },
  { to: "/case-studies#ads", label: "Google Ads" },
  { to: "/case-studies#websites", label: "Websites" },
];

const linkClass = "block text-2xl font-extrabold no-underline text-white/85 hover:text-[#E8A33D] transition-colors py-3";

const MobileNav = ({ active = null }: { active?: ActiveNav }) => {
  const [open, setOpen] = useState(false);
  const [caseStudiesOpen, setCaseStudiesOpen] = useState(false);

  const close = () => setOpen(false);

  return (
    <div className="md:hidden">
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Trigger asChild>
          <button
            type="button"
            aria-label="Open menu"
            className="flex items-center justify-center w-10 h-10 text-white"
          >
            <Menu className="w-7 h-7" />
          </button>
        </Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[100] bg-[#081627] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <Dialog.Content
            className="fixed inset-0 z-[101] flex flex-col overflow-y-auto px-8 py-6 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            style={{ background: "#081627", fontFamily: "'Archivo', sans-serif" }}
          >
            <Dialog.Title className="sr-only">Site navigation</Dialog.Title>
            <div className="flex justify-end mb-8">
              <Dialog.Close asChild>
                <button type="button" aria-label="Close menu" className="flex items-center justify-center w-10 h-10 text-white">
                  <X className="w-7 h-7" />
                </button>
              </Dialog.Close>
            </div>

            <nav className="flex flex-col flex-1">
              <Link to="/#services" onClick={close} className={linkClass} style={active === "services" ? { color: ACCENT } : undefined}>
                Services
              </Link>
              <Link to="/reviews" onClick={close} className={linkClass} style={active === "reviews" ? { color: ACCENT } : undefined}>
                Reviews
              </Link>
              <Link to="/plans" onClick={close} className={linkClass} style={active === "plans" ? { color: ACCENT } : undefined}>
                Plans
              </Link>

              <Collapsible open={caseStudiesOpen} onOpenChange={setCaseStudiesOpen}>
                <CollapsibleTrigger
                  className={`${linkClass} w-full flex items-center justify-between`}
                  style={active === "case-studies" ? { color: ACCENT } : undefined}
                >
                  Case Studies
                  <ChevronDown className={`w-5 h-5 transition-transform ${caseStudiesOpen ? "rotate-180" : ""}`} />
                </CollapsibleTrigger>
                <CollapsibleContent className="flex flex-col pl-4">
                  {caseStudyLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={close}
                      className="block text-lg font-semibold no-underline text-white/70 hover:text-[#E8A33D] transition-colors py-2.5"
                    >
                      {link.label}
                    </Link>
                  ))}
                </CollapsibleContent>
              </Collapsible>

              <Link to="/about" onClick={close} className={linkClass} style={active === "about" ? { color: ACCENT } : undefined}>
                About
              </Link>
              <Link to="/contact" onClick={close} className={linkClass} style={active === "contact" ? { color: ACCENT } : undefined}>
                Contact
              </Link>
            </nav>

            <Link
              to="/book"
              onClick={close}
              className="inline-flex items-center justify-center font-extrabold text-lg px-6 py-4 rounded-full no-underline mb-4"
              style={{ background: ACCENT, color: "#081627" }}
            >
              Book a Call
            </Link>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};

export default MobileNav;
